import React, { useEffect, useState, useMemo } from "react";
import { Modal } from "@material-ui/core";
import { CircularProgress } from "@material-ui/core";
import { apiReports } from "../api/reports";
import CountyChoroplethMap from "./CountyChoroplethMap";

const green = "#94ba33";
const amber = "rgba(251,191,36)";
const red = "#d71433";
const pct = (v) => `${Math.round((v || 0) * 100)}%`;
const utilColor = (u) => u >= 0.7 ? green : u >= 0.4 ? amber : red;
// Clearance = completed / scheduled. "Did the inspector clear what was dated this window?"
const clearance = (done, sched) => sched ? done / sched : null;
const clearancePct = (done, sched) => { const c = clearance(done, sched); return c == null ? "—" : pct(c); };
const clearanceColor = (done, sched) => { const c = clearance(done, sched); return c == null ? null : c >= 0.9 ? green : c >= 0.7 ? amber : red; };

const METRICS = [
  { key: "complete", label: "Complete" },
  { key: "outstanding", label: "Outstanding" },
  { key: "incoming", label: "Incoming" },
];

// AreaUtilisationDetailModal — drill-down for a single area row.
// County choropleth (metric toggle) + per-county table + assigned inspectors.
const AreaUtilisationDetailModal = ({ open, onClose, workAreaId, days, capacityWindow, utilisation }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metric, setMetric] = useState("complete");

  useEffect(() => {
    if (!open || workAreaId == null) {return;} // 0 is the Unassigned sentinel
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    apiReports.getAreaUtilisationDetail(workAreaId, days)
      .then((res) => {
        if (cancelled) {return;}
        const r = res?.data?.result;
        if (res?.data?.status === 200 && r) {
          setDetail(r);
        } else {
          setError(res?.data?.message || "Failed to load detail.");
        }
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError("Failed to load detail."); setLoading(false); } });
    return () => { cancelled = true; };
  }, [open, workAreaId, days]);

  const counties = detail?.counties || [];
  const sortedCounties = useMemo(
    () => [...counties].sort((a, b) => (b[metric] || 0) - (a[metric] || 0)),
    [counties, metric]
  );
  const maxCounty = Math.max(1, ...counties.map((c) => c[metric] || 0));

  return (
    <Modal open={open} onClose={onClose}>
      <div style={{
        top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        position: "absolute", maxWidth: 1040, width: "94%",
        maxHeight: "90vh", overflowY: "auto",
      }} className="tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-6 tw-outline-none">
        <div className="tw-flex tw-justify-between tw-items-start tw-mb-4">
          <div>
            <h2 className="tw-text-2xl tw-font-semibold">{detail?.name || "Area"}</h2>
            {detail && 
              <div className="tw-text-sm tw-text-gray-600 tw-mt-1">
                Last {detail.windowDays} days
                {capacityWindow != null && <> &nbsp;·&nbsp; Window capacity: <b>{capacityWindow}</b></>}
              </div>
            }
          </div>
          <button onClick={onClose} className="tw-text-xl tw-px-2">✕</button>
        </div>

        {loading && <div className="tw-flex tw-justify-center tw-py-10"><CircularProgress /></div>}
        {error && <div className="tw-text-fs-red tw-py-6 tw-text-center">{error}</div>}

        {detail && 
          <>
            <div className={`tw-grid tw-grid-cols-2 ${utilisation != null ? "lg:tw-grid-cols-4" : "lg:tw-grid-cols-3"} tw-gap-3 tw-mb-2`}>
              <Stat label="Arrivals" value={detail.incoming} color="tw-bg-primary" />
              <Stat label="Scheduled" value={detail.appointments} color="tw-bg-fs-dark-blue-light" />
              <Stat label="Completed" value={detail.complete} color="tw-bg-fs-green" />
              {utilisation != null && <Stat label="Utilised" value={pct(utilisation)} color="tw-bg-fs-dark-blue" />}
            </div>
            <div className="tw-text-xs tw-text-gray-600 tw-mb-3">
              {detail.appointments > 0
                ? <>Clearance: <b>{pct(detail.complete / detail.appointments)}</b> completed of scheduled &nbsp;·&nbsp; </>
                : <>No appointments dated in window &nbsp;·&nbsp; </>}
              Arrivals = created in window
            </div>

            {/* Open backlog split — diagnoses "more inspectors vs not enough work" */}
            {detail.open && <OpenBacklog open={detail.open} />}

            {/* Metric toggle */}
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
              <span className="tw-text-sm tw-text-gray-600">Shade counties by:</span>
              {METRICS.map((m) => 
                <button
                  key={m.key}
                  onClick={() => setMetric(m.key)}
                  className={`tw-px-3 tw-py-1 tw-rounded-lg tw-text-sm ${
                    metric === m.key ? "tw-bg-primary tw-text-white" : "tw-bg-gray-200 tw-text-gray-700"
                  }`}
                >
                  {m.label}
                </button>
              )}
            </div>

            <div className="tw-bg-gray-50 tw-p-3 tw-rounded-lg tw-mb-6">
              {counties.length > 0
                ? <CountyChoroplethMap counties={counties} metric={metric} />
                : <div className="tw-text-gray-500 tw-text-sm tw-py-10 tw-text-center">No counties assigned to this area.</div>}
            </div>

            <div className="tw-grid md:tw-grid-cols-2 tw-gap-6">
              {/* Per-county table */}
              <div>
                <h3 className="tw-font-semibold tw-mb-2">Counties</h3>
                <div className="tw-overflow-y-auto" style={{ maxHeight: 320 }}>
                  <table className="tw-min-w-full tw-text-sm">
                    <thead className="tw-sticky tw-top-0 tw-bg-white">
                      <tr className="tw-border-b tw-border-gray-200">
                        <th className="tw-px-2 tw-py-1 tw-text-left">County</th>
                        <th className="tw-px-2 tw-py-1 tw-text-right">Complete</th>
                        <th className="tw-px-2 tw-py-1 tw-text-right">Outstanding</th>
                        <th className="tw-px-2 tw-py-1 tw-text-right">Incoming</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCounties.map((c) => 
                        <tr key={`${c.state}-${c.county}`} className="tw-border-b tw-border-gray-100">
                          <td className="tw-px-2 tw-py-1">
                            {c.county}
                            <span className="tw-text-gray-400 tw-text-xs">, {c.state}</span>
                          </td>
                          <td className="tw-px-2 tw-py-1 tw-text-right">{c.complete}</td>
                          <td className="tw-px-2 tw-py-1 tw-text-right">{c.outstanding}</td>
                          <td className="tw-px-2 tw-py-1 tw-text-right">{c.incoming}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Inspectors */}
              <div>
                {(() => {
                  const all = detail.inspectors || [];
                  const assigned = all.filter((i) => !i.outOfArea);
                  const oob = all.filter((i) => i.outOfArea);
                  return <div className="tw-overflow-y-auto" style={{ maxHeight: 360 }}>
                    <h3 className="tw-font-semibold tw-mb-2">Assigned inspectors</h3>
                    {assigned.length === 0
                      ? <p className="tw-text-gray-500 tw-text-sm">No inspectors scheduled in this area.</p>
                      : <table className="tw-min-w-full tw-text-sm">
                        <thead>
                          <tr className="tw-border-b tw-border-gray-200">
                            <th className="tw-px-2 tw-py-1 tw-text-left">Inspector</th>
                            <th className="tw-px-2 tw-py-1 tw-text-right">Days</th>
                            <th className="tw-px-2 tw-py-1 tw-text-right">Sched</th>
                            <th className="tw-px-2 tw-py-1 tw-text-right">Done</th>
                            <th className="tw-px-2 tw-py-1 tw-text-right">Clear</th>
                            <th className="tw-px-2 tw-py-1 tw-text-right">Util</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assigned.map((ins) =>
                            <tr key={ins.userId} className="tw-border-b tw-border-gray-100">
                              <td className="tw-px-2 tw-py-1">{ins.name}</td>
                              <td className="tw-px-2 tw-py-1 tw-text-right">{ins.daysInArea}</td>
                              <td className="tw-px-2 tw-py-1 tw-text-right">{ins.appointments}</td>
                              <td className="tw-px-2 tw-py-1 tw-text-right">{ins.completions}</td>
                              <td className="tw-px-2 tw-py-1 tw-text-right">
                                <span style={{ color: clearanceColor(ins.completions, ins.appointments) || "inherit", fontWeight: 600 }}>
                                  {clearancePct(ins.completions, ins.appointments)}
                                </span>
                              </td>
                              <td className="tw-px-2 tw-py-1 tw-text-right">
                                <span style={{ color: utilColor(ins.utilisation), fontWeight: 600 }}>
                                  {ins.daysInArea > 0 ? pct(ins.utilisation) : "—"}
                                </span>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>}
                    {oob.length > 0 && <>
                      <h3 className="tw-font-semibold tw-mb-1 tw-mt-4">Out-of-area work</h3>
                      <p className="tw-text-gray-500 tw-text-xs tw-mb-2">Completed in these counties by inspectors not scheduled in this area.</p>
                      <table className="tw-min-w-full tw-text-sm">
                        <thead>
                          <tr className="tw-border-b tw-border-gray-200">
                            <th className="tw-px-2 tw-py-1 tw-text-left">Inspector</th>
                            <th className="tw-px-2 tw-py-1 tw-text-right">Sched</th>
                            <th className="tw-px-2 tw-py-1 tw-text-right">Done</th>
                            <th className="tw-px-2 tw-py-1 tw-text-right">Clear</th>
                          </tr>
                        </thead>
                        <tbody>
                          {oob.map((ins) =>
                            <tr key={ins.userId} className="tw-border-b tw-border-gray-100">
                              <td className="tw-px-2 tw-py-1">
                                {ins.name || `User ${ins.userId}`}
                                <span className="tw-ml-2 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-xs tw-bg-amber-100 tw-text-amber-700">out of area</span>
                              </td>
                              <td className="tw-px-2 tw-py-1 tw-text-right">{ins.appointments}</td>
                              <td className="tw-px-2 tw-py-1 tw-text-right">{ins.completions}</td>
                              <td className="tw-px-2 tw-py-1 tw-text-right">
                                <span style={{ color: clearanceColor(ins.completions, ins.appointments) || "inherit", fontWeight: 600 }}>
                                  {clearancePct(ins.completions, ins.appointments)}
                                </span>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </>}
                  </div>;
                })()}
              </div>
            </div>
          </>
        }
      </div>
    </Modal>
  );
};

const Stat = ({ label, value, color }) => 
  <div className={`tw-rounded-lg tw-p-3 tw-text-white ${color}`}>
    <div className="tw-text-2xl tw-font-bold">{value}</div>
    <div className="tw-text-xs tw-uppercase tw-tracking-wider tw-opacity-90">{label}</div>
  </div>
;

// OpenBacklog splits the area's open inspections by scheduling state so the
// popup answers "more inspectors or not enough work?" instead of one lump.
const OpenBacklog = ({ open }) => {
  const o = open || {};
  const verdict = (() => {
    if (!o.total) { return { txt: "Clear", color: green }; }
    const parts = [
      ["future", o.future || 0],
      ["inProgress", o.inProgress || 0],
      ["notStarted", o.notStarted || 0],
      ["overdue7", o.overdue7 || 0],
      ["overdue30", o.overdue30 || 0],
      ["overdueStale", o.overdueStale || 0],
    ];
    const dom = parts.sort((a, b) => b[1] - a[1])[0];
    switch (dom[0]) {
    case "future": return { txt: "Healthy", color: green };
    case "inProgress": return { txt: "In flight", color: green };
    case "notStarted": return { txt: "Scheduling gap", color: amber };
    case "overdue7": return { txt: "Behind this week", color: red };
    case "overdue30": return { txt: "Slipping", color: amber };
    default: return { txt: "Stale pile", color: red };
    }
  })();
  return (
    <div className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-mb-6">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
        <div className="tw-text-sm tw-font-semibold">Open backlog: <span className="tw-text-fs-red">{o.total}</span></div>
        <span style={{ background: verdict.color }} className="tw-text-white tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-semibold">{verdict.txt}</span>
      </div>
      <div className="tw-grid tw-grid-cols-4 tw-gap-2">
        <div>
          <div className="tw-text-xs tw-text-gray-500">Not started</div>
          <div className="tw-font-semibold">{o.notStarted}</div>
        </div>
        <div>
          <div className="tw-text-xs tw-text-gray-500">In progress</div>
          <div className="tw-font-semibold">{o.inProgress}</div>
        </div>
        <div>
          <div className="tw-text-xs tw-text-gray-500">Overdue <span className="tw-text-fs-red">({o.overdue})</span></div>
          <div className="tw-font-semibold tw-text-xs">≤7d: {o.overdue7} · 8–30d: {o.overdue30} · &gt;30d: {o.overdueStale}</div>
        </div>
        <div>
          <div className="tw-text-xs tw-text-gray-500">Future appts</div>
          <div className="tw-font-semibold">{o.future}</div>
        </div>
      </div>
    </div>
  );
};

export default AreaUtilisationDetailModal;